import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {Box, IBoxSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IBoxesSnapshot
{
    [boxId:string]:IBoxSnapshot;
}

export interface ISurfaceLayerSnapshot
{
    allBoxes:IBoxesSnapshot;
    boxes:Array<BoxId>;
}

export class SurfaceLayer
{
    @observable
    public allBoxes:Map<BoxId, Box>;

    @observable
    public boxes:Array<Box>;

    private constructor()
    {
    }

    public static create(boxes:Array<Box> = []):SurfaceLayer
    {
        const surfaceLayer:SurfaceLayer = new SurfaceLayer();

        surfaceLayer.allBoxes = new Map();
        surfaceLayer.boxes = [];

        boxes.forEach(surfaceLayer.addBox);

        return surfaceLayer;
    }

    @action.bound
    public addBox(box:Box, insertIndex:number = null):Box
    {
        this.allBoxes.set(box.id, box);

        if( box.parent )
            box.parent.addChild(box, insertIndex);
        else
        {
            if( insertIndex === null )
                this.boxes.push(box);
            else
                this.boxes.splice(insertIndex, 0, box);
        }

        return box;
    }

    @action.bound
    public removeBox(box:Box):void
    {
        if( box.children )
            box.children.forEach(this.removeBox);

        this.allBoxes.delete(box.id);
    }

    @action.bound
    public addRootBox(box:Box, insertIndex:number = null):void
    {
        box.setParent(null);

        if( insertIndex === null )
            this.boxes.push(box);
        else
            this.boxes.splice(insertIndex, 0, box);
    }

    @action.bound
    public removeRootBox(boxIndex:number, isDestroy:boolean = true):void
    {
        const boxId:BoxId = this.boxes[boxIndex].id;

        this.boxes.splice(boxIndex, 1);

        if( isDestroy )
            this.allBoxes.delete(boxId);
    }

    @action.bound
    public clearBoxes():void
    {
        this.boxes.splice(0, this.boxes.length);
        this.allBoxes.clear();
    }

    @action.bound
    public moveChild(childBox:Box, toIndex:number):void
    {
        const boxIndex = this.boxes.findIndex(({id}) => id === childBox.id);
        const isToFirst:boolean = toIndex === 0;
        const isToLast:boolean = toIndex === this.boxes.length - 1;

        this.boxes.splice(boxIndex, 1);

        if( isToFirst )
            this.boxes.unshift(childBox.id as any);
        else if( isToLast )
            this.boxes.push(childBox.id as any);
        else
            this.boxes.splice(toIndex, 0, childBox.id as any);
    }

    @action.bound
    public replaceBoxes(boxes:Array<Box>):void
    {
        (this.boxes as any).replace(boxes);
    }

    @computed
    public get visualBoxes():Array<VisualBox>
    {
        const visualBoxes:Array<VisualBox> = [];

        this.boxes.forEach((child:Box) =>
        {
            visualBoxes.push(new VisualBox(child, null, null));
        });

        return visualBoxes;
    }

    @computed
    public get viewport():Box
    {
        for( let i = 0; i < this.boxes.length; i++ )
        {
            if( this.boxes[i].isViewport )
                return this.boxes[i];
        }

        console.warn('Oups! It looks like there is no viewport on the page');
        return null;
    }

    public static toJson:ITransformer<SurfaceLayer, ISurfaceLayerSnapshot> = createTransformer((surfaceLayer:SurfaceLayer) =>
    {
        return {
            allBoxes: Array.from(surfaceLayer.allBoxes.entries()).reduce((result, [boxId, box]) =>
            {
                result[boxId] = Box.toJson(box);
                return result;
            }, {}),
            boxes: surfaceLayer.boxes.map(({id}) => id)
        };
    });

    public static fromJson(json:ISurfaceLayerSnapshot,
                           page:Page,
                           currentState?:SurfaceLayer,
                           currentJson?:ISurfaceLayerSnapshot,
                           ctx?:DesignStateFromJsonCtx):SurfaceLayer
    {
        if( !currentState )
            return SurfaceLayer.fromJsonDeep(json, page);

        if( !currentJson )
            currentJson = SurfaceLayer.toJson(currentState);

        const surfaceLayer:SurfaceLayer = new SurfaceLayer();

        if( json.allBoxes === currentJson.allBoxes )
        {
            surfaceLayer.allBoxes = currentState.allBoxes;
            surfaceLayer.allBoxes.forEach((box:Box) => box.page = page);
        }
        else
        {
            const allBoxes:Map<BoxId, Box> = new Map();

            Object.entries(json.allBoxes).forEach(([boxId, box]) =>
            {
                const boxCurrentState:Box = currentState.allBoxes.get(boxId);
                const boxCurrentJson:IBoxSnapshot = currentJson.allBoxes[boxId];

                if( json.allBoxes[boxId] === currentJson.allBoxes[boxId] )
                {
                    boxCurrentState.page = page;
                    allBoxes.set(boxId, boxCurrentState);
                }
                else
                    allBoxes.set(boxId, Box.fromJson(box, page, boxCurrentState, boxCurrentJson, ctx));
            });

            allBoxes.forEach((box:Box) =>
            {
                if( box.parent )
                    box.parent = allBoxes.get(box.parent.id);

                box.children = box.children.map(({id}) => allBoxes.get(id));
            });

            surfaceLayer.allBoxes = allBoxes;
        }

        surfaceLayer.boxes = json.boxes.map((boxId:BoxId) => surfaceLayer.allBoxes.get(boxId));

        return surfaceLayer;
    }

    public static fromJsonDeep(json:ISurfaceLayerSnapshot, page:Page):SurfaceLayer
    {
        const surfaceLayer:SurfaceLayer = new SurfaceLayer();

        const allBoxes:Map<BoxId, Box> = new Map();

        Object.entries(json.allBoxes).forEach(([boxId, box]) => allBoxes.set(boxId, Box.fromJsonDeep(box, page)));

        allBoxes.forEach((box:Box) =>
        {
            if( box.parent )
                box.parent = allBoxes.get(box.parent.id);

            box.children = box.children.map(({id}) => allBoxes.get(id));
        });

        surfaceLayer.allBoxes = allBoxes;
        surfaceLayer.boxes = json.boxes.map((boxId:BoxId) => allBoxes.get(boxId));

        return surfaceLayer;
    }
}
